import React, { useContext, useMemo, useState } from 'react';
import { Button, Col, Form, Input, Modal, Row } from 'antd';

import GenericAutoComplete from '@totem/components/common/autoCompletes/GenericAutoComplete';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { Activity } from '@totem/components/ticketing/ticketing';
import { defaultActivity } from '@totem/components/ticketing/utilities';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { Attribute } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';

export const REQUEST_APPROVAL_ACTIVITY_ID = '00044055f3329dc06bba9905';

const FormItem = Form.Item;
const { TextArea } = Input;

const styles = {
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
  previewFrame: {
    width: '100%',
    height: '420px',
    border: '1px solid rgba(148, 148, 148, 0.45)',
    borderRadius: '4px',
    background: '#fff',
  },
  previewLabel: {
    marginBottom: '0.5rem',
    fontWeight: 'bold' as const,
  },
};

// Mirrors sendgrid-library/data/templates/generic-ticket-approval-required —
// keep the visible fields in sync with that template so this preview matches
// what the approver actually receives.
const buildPreviewHtml = (values: {
  ticketNumber: string;
  ticketStatus: string;
  activityTypeDisplay: string;
  organizationName: string;
  buildingName: string;
  ticketSummary: string;
  ticketDescription: string;
  note: string;
  ticketLink: string;
}) => `
<html>
  <head>
    <style>
      body { font-family: "Open Sans", sans-serif; font-size: 14px; margin: 0; padding: 0 0; }
      .border { height: 5px; width: 100%; background: linear-gradient(90deg, #309cd9, #5ecbee); }
      h1 { font-weight: bold; font-size: 36px; text-align: center; padding-top: 20px; padding-bottom: 0px; margin-bottom: 0px; }
      h1 a { text-decoration: none; }
      .header-tagline { text-align: center; font-size: 20px; padding-top: 0px; margin-top: 0px; }
      .content-container { padding: 0 50px; }
      footer { text-align: center; padding: 50px 0; border-top: 1px solid rgba(148, 148, 148, 0.45); margin-top: 50px; }
      footer p { padding: 0; margin: 0; color: #757575; font-weight: 500; }
      .website, .website:visited, .website:active { color: #757575; font-weight: 500; text-decoration: none; }
    </style>
  </head>
  <body>
    <div class="border"></div>
    <div class="container">
      <h1>Approval Required</h1>
      <h1>Ticket: <a href="${values.ticketLink}">${values.ticketNumber}</a></h1>
      <div class="header-tagline">${values.ticketStatus}</div>
      <div class="content-container">
        <h2>${values.activityTypeDisplay}</h2>
        <b>Message</b><br />${values.note}<br/><br/>
        <b>Organization</b><br/> ${values.organizationName}<br/><br/>
        <b>Building</b><br/> ${values.buildingName}<br/><br/>
        <b>Summary</b><br/> ${values.ticketSummary}<br/><br/>
        <b>Description</b><br/> ${values.ticketDescription}<br/><br/>
        <footer>
          <p>Intelligent Buildings</p>
          <p>15105 John J Delaney Drive, Charlotte NC 28277</p>
          <a class="website" href="https://intelligentbuildings.com">intelligentbuildings.com</a>
        </footer>
      </div>
    </div>
    <div class="border"></div>
  </body>
</html>
`;

const ApprovalCreateDialog = () => {
  const { ticket, onAction, onCreateActivity, activityConfiguration } =
    useContext(TicketContext);
  const [approverName, setApproverName] = useState<string>('');
  const [approverEmail, setApproverEmail] = useState<string>('');
  const [approverPhone, setApproverPhone] = useState<string>('');
  const [approvalSummary, setApprovalSummary] = useState<string>('');
  const [approvalDescription, setApprovalDescription] = useState<string>('');
  const [formSubmitted, setFormSubmitted] = useState(false);

  const activityConfig = activityConfiguration.find(
    (cfg) => cfg.id === REQUEST_APPROVAL_ACTIVITY_ID,
  );

  const validate = () => {
    return (
      approverName.trim() !== '' &&
      approverEmail.trim() !== '' &&
      approvalDescription.trim() !== ''
    );
  };

  const handleClose = () => {
    onAction('close-activity-dialog', '');
  };

  const handleSubmit = () => {
    setFormSubmitted(true);

    if (!validate() || !isNotNull(activityConfig)) {
      return;
    }

    const attributes: Attribute[] = [
      {
        name: 'party-involved-name',
        label: 'Approver Name',
        value: approverName,
        display: true,
        id: '',
      },
      {
        name: 'party-involved-email',
        label: 'Email',
        value: approverEmail,
        display: true,
        id: '',
      },
      {
        name: 'party-involved-phone',
        label: 'Phone',
        value: approverPhone,
        display: true,
        id: '',
      },
      {
        name: 'approval-summary',
        label: 'Approval Request Summary',
        value: approvalSummary,
        display: true,
        id: '',
      },
      {
        name: 'approval-description',
        label: 'Approval Request Details',
        value: approvalDescription,
        display: true,
        id: '',
      },
    ];

    const activity: Activity = {
      ...defaultActivity,
      type: {
        id: activityConfig.id,
        code: activityConfig.code,
        display: activityConfig.display,
      },
      attributes,
      createdAt: new Date().toISOString(),
    };

    onCreateActivity(activity);
  };

  const previewHtml = useMemo(() => {
    if (!isNotNull(ticket)) {
      return '';
    }

    return buildPreviewHtml({
      ticketNumber: ticket.ticket.number,
      ticketStatus: ticket.ticket.status,
      activityTypeDisplay: isNotNull(activityConfig)
        ? activityConfig.display
        : 'Request Approval',
      organizationName: isNotNull(ticket.organization)
        ? ticket.organization.name
        : '',
      buildingName: isNotNull(ticket.building) ? ticket.building.name : '',
      ticketSummary: ticket.ticket.summary,
      ticketDescription: ticket.ticket.description,
      note:
        approvalDescription.trim() !== ''
          ? approvalDescription
          : '<i>(the Approval Request Details you enter will appear here)</i>',
      ticketLink: `${window.location.origin}/dashboard/ticket/${ticket.ticket.id}`,
    });
  }, [ticket, activityConfig, approvalDescription]);

  return (
    <Modal
      open
      onCancel={handleClose}
      title={<ModalTitle>Add Approval</ModalTitle>}
      width={'70%'}
      footer={
        <ModalFooter>
          <Button onClick={handleClose}>Close</Button>
          <Button type="primary" onClick={handleSubmit}>
            Send Approval Request
          </Button>
        </ModalFooter>
      }
    >
      <Row gutter={24}>
        <Col span={11}>
          <Form layout="vertical">
            <FormItem
              label="Approver Name"
              colon={false}
              style={styles.formItem}
              required
              validateStatus={
                formSubmitted === false || approverName.trim() !== ''
                  ? 'success'
                  : 'error'
              }
            >
              <Input
                value={approverName}
                onChange={(evt) => setApproverName(evt.target.value)}
              />
            </FormItem>
            <FormItem
              label="Email"
              colon={false}
              style={styles.formItem}
              required
              validateStatus={
                formSubmitted === false || approverEmail.trim() !== ''
                  ? 'success'
                  : 'error'
              }
            >
              <GenericAutoComplete
                type={'User'}
                label={''}
                limit={15}
                onSelect={(value) => setApproverEmail(value.display)}
                onChange={setApproverEmail}
                allowClear
                allowFreeform={false}
                selectedValue={
                  isNotNull(approverEmail)
                    ? { display: approverEmail, value: EMPTY_ID }
                    : { display: '', value: '' }
                }
              />
            </FormItem>
            <FormItem label="Phone" colon={false} style={styles.formItem}>
              <Input
                value={approverPhone}
                onChange={(evt) => setApproverPhone(evt.target.value)}
              />
            </FormItem>
            <FormItem
              label="Approval Request Summary"
              colon={false}
              style={styles.formItem}
            >
              <Input
                value={approvalSummary}
                onChange={(evt) => setApprovalSummary(evt.target.value)}
              />
            </FormItem>
            <FormItem
              label="Approval Request Details"
              colon={false}
              style={styles.formItem}
              required
              validateStatus={
                formSubmitted === false || approvalDescription.trim() !== ''
                  ? 'success'
                  : 'error'
              }
            >
              <TextArea
                rows={5}
                value={approvalDescription}
                onChange={(evt) => setApprovalDescription(evt.target.value)}
              />
            </FormItem>
          </Form>
        </Col>
        <Col span={13}>
          <div style={styles.previewLabel}>Email Preview</div>
          <iframe
            title="Approval Email Preview"
            srcDoc={previewHtml}
            style={styles.previewFrame}
            sandbox=""
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default ApprovalCreateDialog;
